import { useState, useEffect, useMemo } from 'react';
import { Button, Card, Descriptions, Table, message, Spin, Typography, Row, Col, Divider, Checkbox, Space, Tag, Input } from 'antd';
import { CheckCircleOutlined, WhatsAppOutlined, DownloadOutlined } from '@ant-design/icons';
import { getStudentReportCard, saveReportCardNote } from '../services/studentService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import useResponsive from '../hooks/useResponsive';
import './StudentProfilePage.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const StudentReportCardView = ({ studentId }) => {
    const id = studentId;
    const { isMobile } = useResponsive(); // Use the hook

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedExams, setSelectedExams] = useState({});
    const [teacherNote, setTeacherNote] = useState({});

    const renderData = (value) => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && isNaN(value))) {
            return 'N/A';
        }
        return value;
    };

    const formatScore = (value) => {
        const num = parseFloat(value);
        if (isNaN(num) || value === null || value === undefined) return '-';
        if (num % 1 === 0) {
            return num.toFixed(0);
        }
        return num.toFixed(2);
    };

    const getExamKey = (year, exam, idx) => {
        if (exam && typeof exam === 'object' && exam.id !== undefined && exam.id !== null) return String(exam.id);
        let namePart = 'exam';
        if (typeof exam === 'string') {
            namePart = exam.replace(/\s+/g, '_');
        } else if (exam && typeof exam === 'object' && exam.name) {
            namePart = String(exam.name).replace(/\s+/g, '_');
        }
        return `${year}-${namePart}-${idx}`;
    };

    useEffect(() => {
        const fetchReportData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const data = await getStudentReportCard(id);
                setReportData(data);
                // console.log('Fetched report card data:', data);

                const initialSelected = {};
                const initialNotes = {};

                if (data.reportData) {
                    Object.keys(data.reportData).forEach(year => {
                        const exams = data.reportData[year].exams || [];
                        initialSelected[year] = exams.map((exam, idx) => getExamKey(year, exam, idx));
                        initialNotes[year] = data.reportData[year].noteContent || '';
                    });
                }
                setSelectedExams(initialSelected);
                setTeacherNote(initialNotes);

            } catch (error) {
                message.error('Failed to load student report card.');
            } finally {
                setLoading(false);
            }
        };
        fetchReportData();
    }, [id]);

    const handleDownloadPDF = () => {
        if (!reportData || !reportData.studentDetails) {
            message.error('No report data available');
            return;
        }

        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 15;
            let yPos = margin;

            const { studentDetails, reportData: yearReports } = reportData;

            // ===== 1. REPORT HEADER =====
            doc.setFontSize(18);
            doc.setFont(undefined, 'bold');
            doc.text('KALLADI ABDU HAJI HIGHER SECONDARY SCHOOL', pageWidth / 2, yPos, { align: 'center' });
            yPos += 8;

            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            doc.text('Student Academic Report Card', pageWidth / 2, yPos, { align: 'center' });
            yPos += 5;

            doc.setFontSize(9);
            doc.setTextColor(150);
            doc.text(`Report Generated: ${dayjs().format('DD-MM-YYYY')}`, pageWidth / 2, yPos, { align: 'center' });
            yPos += 12;
            doc.setTextColor(0);


            // ===== 2. STUDENT DETAILS TABLE =====
            const studentInfo = [
                ['Name:', renderData(studentDetails.student_name), 'Admission No:', renderData(studentDetails.admission_no)],
                ['Class:', renderData(studentDetails.class_name), 'Roll No:', renderData(studentDetails.roll_no)],
                ['Parent:', renderData(studentDetails.parent_name), 'DOB:', studentDetails.dob ? dayjs(studentDetails.dob).format('DD-MM-YYYY') : 'N/A'],
                ['Status:', studentDetails.is_current ? 'CURRENT' : 'PREVIOUS', 'Contact:', renderData(studentDetails.phone_number)],
            ];

            autoTable(doc, {
                body: studentInfo,
                startY: yPos,
                theme: 'plain',
                styles: { fontSize: 9, cellPadding: 1.5 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 25 },
                    1: { cellWidth: 65 },
                    2: { fontStyle: 'bold', cellWidth: 25 },
                    3: { cellWidth: 'auto' },
                }
            });

            yPos = doc.lastAutoTable.finalY + 15; // Get position after the student info table


            // ===== 3. ACADEMIC PERFORMANCE SECTIONS =====
            Object.values(yearReports || {}).forEach((yearData) => {
                if (!selectedExams[yearData.academicYear]?.length > 0) return;

                // Check for page break before adding a new year section
                if (yPos > doc.internal.pageSize.getHeight() - 60) {
                    doc.addPage();
                    yPos = margin;
                }

                // Year Title
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text(`${renderData(yearData.academicYear)} - Academic Performance`, margin, yPos);
                yPos += 8;

                const subjectNames = Array.from(Object.keys(yearData.subjects || {})).sort();
                const selectedExamData = (yearData.exams || [])
                    .map((exam, idx) => ({
                        examName: typeof exam === 'string' ? exam : (exam && exam.name ? exam.name : `Exam ${idx + 1}`),
                        examKey: getExamKey(yearData.academicYear, exam, idx),
                    }))
                    .filter(exam => selectedExams[yearData.academicYear]?.includes(exam.examKey));

                // --- TABLE HEADERS ---
                const head = [
                    { content: 'Exam/Term', styles: { halign: 'left' } },
                    ...subjectNames.map(s => ({ content: s, styles: { halign: 'center' } })),
                    { content: 'Total', styles: { halign: 'center' } }
                ];

                // --- TABLE BODY ---
                const body = [];
                let grandTotalScored = 0;
                let grandTotalMax = 0;

                selectedExamData.forEach(examData => {
                    const row = [{ content: examData.examName, styles: { fontStyle: 'bold', halign: 'left' } }];
                    const examTotal = calculateExamTotal(examData.examName, yearData);
                    let examTotalScored = examTotal.scored;
                    let examTotalMax = examTotal.max;

                    subjectNames.forEach(subjectName => {
                        const subject = yearData.subjects[subjectName];
                        const mark = subject?.scores[examData.examName];

                        if (mark && mark.score !== null) {
                            row.push({ content: `${formatScore(mark.score)} / ${formatScore(mark.total)}`, styles: { halign: 'center' } });
                        } else {
                            row.push({ content: '-', styles: { halign: 'center' } });
                        }
                    });

                    grandTotalScored += examTotalScored;
                    grandTotalMax += examTotalMax;

                    row.push({ content: `${formatScore(examTotalScored)} / ${formatScore(examTotalMax)}`, styles: { fontStyle: 'bold', halign: 'center' } });
                    body.push(row);
                });

                // --- TABLE FOOTER (SUMMARY) ---
                const summaryRow = [{ content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left', fillColor: '#f0f0f0' } }];
                subjectNames.forEach(subjectName => {
                    const total = calculateSubjectTotal(subjectName, yearData);
                    summaryRow.push({ content: `${formatScore(total.scored)} / ${formatScore(total.max)}`, styles: { fontStyle: 'bold', halign: 'center', fillColor: '#f0f0f0' } });
                });

                const grandTotal = calculateGrandTotal(yearData);
                grandTotalScored = grandTotal.scored;
                grandTotalMax = grandTotal.max;

                summaryRow.push({ content: `${formatScore(grandTotalScored)} / ${formatScore(grandTotalMax)}`, styles: { fontStyle: 'bold', halign: 'center', fillColor: '#e6f7ff' } });
                body.push(summaryRow);

                autoTable(doc, {
                    head: [head],
                    body: body,
                    startY: yPos,
                    theme: 'grid',
                    headStyles: { fillColor: '#333', textColor: '#fff', fontStyle: 'bold', halign: 'center' },
                    didDrawPage: (data) => {
                        yPos = data.cursor.y; // Update yPos after table is drawn
                    }
                });

                yPos = doc.lastAutoTable.finalY + 8;

                // --- GRAND AVERAGE ---
                const average = grandTotalMax > 0 ? ((grandTotalScored / grandTotalMax) * 100).toFixed(2) : '-';
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                doc.text(`Grand Average: ${average}%`, margin, yPos);
                yPos += 10;

                // --- TEACHER'S NOTE ---
                const noteContent = teacherNote[yearData.academicYear];
                if (noteContent && noteContent.trim()) {
                    doc.setFont(undefined, 'bold');
                    doc.text("Teacher's Note:", margin, yPos);
                    yPos += 5;
                    doc.setFont(undefined, 'normal');
                    const splitNote = doc.splitTextToSize(noteContent, pageWidth - (margin * 2));
                    doc.text(splitNote, margin, yPos);
                    yPos += (splitNote.length * 5) + 5;
                }
            });


            // ===== 4. SIGNATURES =====
            const finalY = doc.internal.pageSize.getHeight() - 30; // Position signatures at the bottom
            doc.setFontSize(10);
            doc.text(`${studentDetails.class_teacher_name || 'Class Teacher'}`, margin, finalY);
            doc.text('Principal', pageWidth - margin, finalY, { align: 'right' });
            doc.setLineWidth(0.5);
            doc.line(margin, finalY - 7, margin + 40, finalY - 7); // Line for teacher, 5px gap
            doc.line(pageWidth - margin - 40, finalY - 7, pageWidth - margin, finalY - 7); // Line for principal, 5px gap


            // ===== 5. SAVE DOCUMENT =====
            const fileName = `${studentDetails.student_name}_Report_${dayjs().format('YYYY-MM-DD')}.pdf`;
            doc.save(fileName);
            message.success('Report card downloaded successfully!');

        } catch (error) {
            console.error('PDF generation error:', error);
            message.error('Failed to generate PDF report card.');
        }
    };


    const handleSaveNote = async (year, note) => {
        try {
            await saveReportCardNote(id, year, note);
            message.success(`Note saved successfully for ${year}`);
            setTeacherNote(prev => ({ ...prev, [year]: note }));
        } catch (error) {
            message.error('Failed to save note');
        }
    };

    const handleShareReport = () => {
        if (!reportData || !reportData.studentDetails) return;

        const studentName = reportData.studentDetails.student_name;
        const currentClass = reportData.studentDetails.class_name;
        const reportUrl = `${window.location.origin}/students/${id}`;

        let messageText = `*Academic Report Card Available*\n\n`;
        messageText += `Student: ${studentName}\n`;
        messageText += `Class: ${currentClass || 'N/A'}\n\n`;
        messageText += `View full profile and academic history here: ${reportUrl}\n\n`;
        messageText += `(Requires portal login to view)`;

        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(messageText)}`;
        window.open(whatsappUrl, '_blank');
    };

    // Helper functions for calculations
    const calculateExamTotal = (examName, yearData) => {
        let totalScored = 0;
        let totalMax = 0;

        Object.values(yearData.subjects || {}).forEach(subject => {
            const mark = subject.scores?.[examName];
            if (mark && mark.score !== null && mark.score !== undefined) {
                totalScored += parseFloat(mark.score) || 0;
                totalMax += parseFloat(mark.total) || 0;
            }
        });

        return { scored: totalScored, max: totalMax };
    };

    const calculateSubjectTotal = (subjectName, yearData) => {
        const subject = yearData.subjects?.[subjectName];
        let totalScored = 0;
        let totalMax = 0;

        Object.values(subject?.scores || {}).forEach(mark => {
            if (mark && mark.score !== null && mark.score !== undefined) {
                totalScored += parseFloat(mark.score) || 0;
                totalMax += parseFloat(mark.total) || 0;
            }
        });

        return { scored: totalScored, max: totalMax };
    };

    const calculateGrandTotal = (yearData) => {
        let grandTotalScored = 0;
        let grandTotalMax = 0;

        Object.values(yearData.subjects || {}).forEach(subject => {
            Object.values(subject.scores || {}).forEach(mark => {
                if (mark && mark.score !== null && mark.score !== undefined) {
                    grandTotalScored += parseFloat(mark.score) || 0;
                    grandTotalMax += parseFloat(mark.total) || 0;
                }
            });
        });

        return { scored: grandTotalScored, max: grandTotalMax };
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;
    }

    if (!reportData || !reportData.studentDetails) {
        return (
            <Card>
                <Title level={4}>Academic Data Not Available</Title>
                <Text type="secondary">
                    Could not find detailed academic records for this student.
                </Text>
            </Card>
        );
    }

    const { studentDetails, reportData: yearReports } = reportData;
    const isCurrentUserClassTeacher = false;

    // --- Mobile Year Report Component ---
    const MobileYearReport = ({ yearData }) => {
        const subjectNames = Array.from(Object.keys(yearData.subjects || {})).sort();
        const selectedExamData = useMemo(() => {
            return (yearData.exams || [])
                .map((exam, idx) => ({
                    examName: typeof exam === 'string' ? exam : (exam && exam.name ? exam.name : `Exam ${idx + 1}`),
                    examKey: getExamKey(yearData.academicYear, exam, idx),
                }))
                .filter(exam => selectedExams[yearData.academicYear]?.includes(exam.examKey));
        }, [yearData.exams, yearData.academicYear]);

        const grandTotal = calculateGrandTotal(yearData);
        const grandAverage = grandTotal.max > 0
            ? ((grandTotal.scored / grandTotal.max) * 100).toFixed(2)
            : '-';

        const isEditable = isCurrentUserClassTeacher && (yearData.academicYear === studentDetails.current_academic_year_name);
        const currentNote = teacherNote[yearData.academicYear] || '';

        return (
            <div className="mobile-report-card">
                <Title level={4} style={{ marginBottom: 16, textAlign: 'center' }}>{renderData(yearData.academicYear)}</Title>

                {selectedExamData.map(examData => {
                    const examTotal = calculateExamTotal(examData.examName, yearData);
                    const percentage = examTotal.max > 0 ? ((examTotal.scored / examTotal.max) * 100).toFixed(1) : '-';

                    return (
                        <Card
                            key={examData.examKey}
                            title={examData.examName}
                            size="small"
                            style={{ marginBottom: 16, borderRadius: 8 }}
                            headStyle={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {subjectNames.map(subjectName => {
                                    const subjectData = yearData.subjects[subjectName];
                                    const mark = subjectData?.scores[examData.examName];
                                    // if (!mark || mark.score === null || mark.score === undefined) return null; // REMOVED: Show all subjects

                                    const scoreDisplay = (mark && mark.score !== null && mark.score !== undefined)
                                        ? <Text strong>{formatScore(mark.score)} <span style={{ color: '#999', fontWeight: 'normal' }}>/ {formatScore(mark.total)}</span></Text>
                                        : <Text type="secondary">-</Text>;

                                    return (
                                        <div key={subjectName} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #f0f0f0', paddingBottom: 4 }}>
                                            <Text>{subjectName}</Text>
                                            {scoreDisplay}
                                        </div>
                                    );
                                })}
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong>Total</Text>
                                    <div style={{ textAlign: 'right' }}>
                                        <Text strong style={{ fontSize: 16 }}>{formatScore(examTotal.scored)} / {formatScore(examTotal.max)}</Text>
                                        <div style={{ fontSize: 12, color: '#666' }}>{percentage}%</div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}

                <Card size="small" style={{ backgroundColor: '#e6f7ff', marginBottom: 16, borderRadius: 8, border: '1px solid #91d5ff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ color: '#0050b3' }}>GRAND AVERAGE</Text>
                        <Text strong style={{ fontSize: 18, color: '#0050b3' }}>{grandAverage}%</Text>
                    </div>
                </Card>

                <Card size="small" title="Teacher's Note" style={{ marginBottom: 16, borderRadius: 8 }}>
                    {isEditable ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <TextArea
                                value={currentNote}
                                onChange={(e) => setTeacherNote(prev => ({ ...prev, [yearData.academicYear]: e.target.value }))}
                                rows={3}
                            />
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => handleSaveNote(yearData.academicYear, currentNote)}
                                block
                            >
                                Save Note
                            </Button>
                        </Space>
                    ) : (
                        <Text type="secondary" style={{ fontStyle: 'italic' }}>{currentNote || 'No note added.'}</Text>
                    )}
                </Card>
            </div>
        );
    };

    // --- Desktop YearReport Component ---
    const YearReport = ({ yearData }) => {
        const subjectNames = Array.from(Object.keys(yearData.subjects || {})).sort();

        const selectedExamData = useMemo(() => {
            return (yearData.exams || [])
                .map((exam, idx) => ({
                    examName: typeof exam === 'string' ? exam : (exam && exam.name ? exam.name : `Exam ${idx + 1}`),
                    examKey: getExamKey(yearData.academicYear, exam, idx),
                }))
                .filter(exam => selectedExams[yearData.academicYear]?.includes(exam.examKey));
        }, [yearData.exams, yearData.academicYear]);

        const totalRowData = useMemo(() => {
            const subjectTotals = {};

            subjectNames.forEach(subjectName => {
                subjectTotals[subjectName] = calculateSubjectTotal(subjectName, yearData);
            });

            const grandTotal = calculateGrandTotal(yearData);

            return {
                subjectTotals,
                grandTotalScored: grandTotal.scored,
                grandTotalMax: grandTotal.max
            };
        }, [subjectNames, yearData]);

        const columns = [
            {
                title: 'Exam/Term',
                dataIndex: 'examName',
                key: 'examName',
                render: (text) => <Text strong>{text}</Text>,
                fixed: 'left',
                width: 150
            },
            ...subjectNames.map(subjectName => ({
                title: subjectName,
                key: subjectName,
                align: 'center',
                width: 150,
                render: (record) => {
                    const subjectData = yearData.subjects[subjectName];
                    const mark = subjectData?.scores[record.examName];

                    if (mark && mark.score !== null && mark.score !== undefined) {
                        return `${formatScore(mark.score)} / ${formatScore(mark.total)}`;
                    }
                    return '-';
                }
            })),
            {
                title: 'Exam Total (Max)',
                key: 'exam_total',
                align: 'center',
                width: 150,
                render: (record) => {
                    const examTotal = calculateExamTotal(record.examName, yearData);
                    return <Text strong>{`${formatScore(examTotal.scored)} / ${formatScore(examTotal.max)}`}</Text>;
                }
            },
        ];

        const dataSource = selectedExamData.map(examData => ({
            key: examData.examKey,
            examName: examData.examName,
        }));

        const isEditable = isCurrentUserClassTeacher && (yearData.academicYear === studentDetails.current_academic_year_name);
        const currentNote = teacherNote[yearData.academicYear] || '';
        const grandAverage = totalRowData.grandTotalMax > 0
            ? ((totalRowData.grandTotalScored / totalRowData.grandTotalMax) * 100).toFixed(2)
            : '-';

        return (
            <div className="report-card-year">
                <Title level={4} className="report-title">{renderData(yearData.academicYear)} SCORE REPORT</Title>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    summary={(pageData) => (
                        <>
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                <Table.Summary.Cell index={0}><Text strong>TOTAL SCORED / MAX</Text></Table.Summary.Cell>
                                {subjectNames.map((subjectName, index) => (
                                    <Table.Summary.Cell key={index} index={index + 1} align="center">
                                        <Text strong>
                                            {formatScore(totalRowData.subjectTotals[subjectName].scored)} / {formatScore(totalRowData.subjectTotals[subjectName].max)}
                                        </Text>
                                    </Table.Summary.Cell>
                                ))}
                                <Table.Summary.Cell index={subjectNames.length + 1} align="center">
                                    <Text strong type="success" style={{ fontSize: '1.1em' }}>
                                        {formatScore(totalRowData.grandTotalScored)} / {formatScore(totalRowData.grandTotalMax)}
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>

                            <Table.Summary.Row style={{ backgroundColor: '#e6f7ff' }}>
                                <Table.Summary.Cell index={0} colSpan={subjectNames.length + 1}>
                                    <Text strong>GRAND AVERAGE %</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={subjectNames.length + 1} align="center">
                                    <Text strong type="success" style={{ fontSize: '1.2em' }}>
                                        {grandAverage} %
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>

                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={columns.length}>
                                    <div className="teacher-note" style={{ padding: '12px 12px 0 12px' }}>
                                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Teacher's Note:</Text>
                                        {isEditable ? (
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <TextArea
                                                    value={currentNote}
                                                    onChange={(e) => setTeacherNote(prev => ({ ...prev, [yearData.academicYear]: e.target.value }))}
                                                    rows={3}
                                                />
                                                <Button
                                                    size="small"
                                                    type="default"
                                                    onClick={() => handleSaveNote(yearData.academicYear, currentNote)}
                                                >
                                                    Save Note
                                                </Button>
                                            </Space>
                                        ) : (
                                            <Text type="secondary">{currentNote || 'No note added.'}</Text>
                                        )}
                                    </div>
                                    <div className="signature-area">
                                        <div className="signature-box">
                                            <Text strong>{renderData(studentDetails.class_teacher_name)}</Text>
                                            <br />
                                            <Text type="secondary">Class Teacher Name & Signature</Text>
                                        </div>
                                        <div className="signature-box">
                                            <Text strong>Principal</Text>
                                            <br />
                                            <Text type="secondary">Name & Signature</Text>
                                        </div>
                                    </div>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </>
                    )}
                />
            </div>
        );
    };

    return (
        <>
            <div className="no-print" style={{ marginBottom: 16 }}>
                <Card>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                        <Col>
                            <Space>
                                <Button
                                    icon={<WhatsAppOutlined />}
                                    onClick={handleShareReport}
                                    style={{ backgroundColor: '#25D366', color: 'white', borderColor: '#25D366' }}
                                >
                                    {isMobile ? 'Share' : 'Share on WhatsApp'}
                                </Button>

                                <Button
                                    type="default"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadPDF}
                                    disabled={Object.values(selectedExams).every(arr => arr.length === 0)}
                                >
                                    {isMobile ? 'PDF' : 'Download PDF'}
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '16px 0' }} />

                    <Title level={4} style={{ marginBottom: 12 }}>Print Options (Select Exams)</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {Object.values(yearReports || {}).map(yearData => (
                            <Card key={yearData.academicYear} size="small" style={{ width: '100%', border: '1px solid #e8e8e8' }}>
                                <Text strong>{renderData(yearData.academicYear)} Exams:</Text>
                                <Divider type="vertical" style={{ margin: '0 8px' }} />
                                <Checkbox.Group
                                    style={{ display: 'inline-block' }}
                                    options={(yearData.exams || []).map((exam, idx) => ({
                                        label: typeof exam === 'string' ? exam : (exam && exam.name ? exam.name : `Exam ${idx + 1}`),
                                        value: getExamKey(yearData.academicYear, exam, idx)
                                    }))}
                                    value={selectedExams[yearData.academicYear] || []}
                                    onChange={(checkedValues) => setSelectedExams(prev => ({ ...prev, [yearData.academicYear]: checkedValues }))}
                                />
                            </Card>
                        ))}
                    </Space>
                </Card>
            </div>

            <div id="print-area" className="print-area-container">
                {Object.values(yearReports || {}).map(yearData => (
                    (selectedExams[yearData.academicYear]?.length > 0) &&
                    <Card key={yearData.academicYear} className="print-card" style={{ marginBottom: 24, padding: isMobile ? '8px' : '24px' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                        <div className="report-header">
                            <Title level={isMobile ? 4 : 3} style={{ margin: 0, textAlign: 'center' }}>KALLADI ABDU HAJI HIGHER SECONDARY SCHOOL</Title>
                            <Text style={{ textAlign: 'center', display: 'block' }}>Student Academic Report</Text>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />

                        <Descriptions bordered column={isMobile ? 1 : 3} size="small" className="report-descriptions">
                            <Descriptions.Item label="Name" span={isMobile ? 1 : 2}>{renderData(studentDetails.student_name)}</Descriptions.Item>
                            <Descriptions.Item label="Gender">{renderData(studentDetails.gender)}</Descriptions.Item>

                            <Descriptions.Item label="Admission No">{renderData(studentDetails.admission_no)}</Descriptions.Item>
                            <Descriptions.Item label="Roll No.">{renderData(studentDetails.roll_no)}</Descriptions.Item>
                            <Descriptions.Item label="Class">{renderData(studentDetails.class_name)}</Descriptions.Item>

                            <Descriptions.Item label="Date of Birth">{studentDetails.dob ? dayjs(studentDetails.dob).format('DD-MM-YYYY') : 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Parent">{renderData(studentDetails.parent_name)}</Descriptions.Item>
                            <Descriptions.Item label="Contact">{renderData(studentDetails.phone_number)}</Descriptions.Item>

                            <Descriptions.Item label="Academic Year">{renderData(yearData.academicYear)}</Descriptions.Item>
                            <Descriptions.Item label="Status" span={isMobile ? 1 : 2}>
                                <Tag color={studentDetails.is_current ? 'success' : 'default'} icon={studentDetails.is_current ? <CheckCircleOutlined /> : null}>
                                    {studentDetails.is_current ? 'CURRENT' : 'PREVIOUS'}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider style={{ margin: '12px 0' }} />

                        {isMobile ? (
                            <MobileYearReport yearData={yearData} />
                        ) : (
                            <YearReport yearData={yearData} />
                        )}
                    </Card>
                ))}
            </div>
        </>
    );
};

export default StudentReportCardView;